import { Box, Button, Grid, Typography } from "@mui/material";
import { Item } from "../../styles/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GET_APP_CONFIG, GET_LEADERBOARD_FOR_ADMIN, GET_LEADERBOARD_FOR_CITIES } from "./leaderboard.queries";
import { useQuery } from "@apollo/client";
import { Table } from "../../common/table/Table";
import { LeaderboardFilter, LeaderboardFilterData } from "./LeaderboardFilter";
import { Popover } from "../../common/popover/Popover";
import { PointHistory } from "../pointHistory/PointHistory";
import { DownloadCsvButton } from "../../common/DownloadCsvButton";
import { config } from "../../config";

const perPage = config.rowsPerPage;

export const Leaderboard: React.FC<any> = (): JSX.Element => {
  let variables = React.useRef<any>({
    pagination: {
      skip: 0,
      take: perPage,
    },
  });
  let variablesCities = React.useRef<any>({
    pagination: {
      skip: 0,
      take: perPage,
    },
  });
  const [rowsPerPage, setRowsPerPage] = useState(perPage);
  const [rowsPerPageCities, setRowsPerPageCities] = useState(perPage);
  const [page, setPage] = useState(0);
  const [pageCities, setPageCities] = useState(0);

  const { data, loading, refetch } = useQuery(GET_LEADERBOARD_FOR_ADMIN, { variables: variables.current });
  const { data: dataCities, loading: loadingCities, refetch: refetchCities } = useQuery(GET_LEADERBOARD_FOR_CITIES, { variables: variablesCities.current });

  const { data: appConfigData } = useQuery(GET_APP_CONFIG);

  const userLevelConfig = useMemo(() => {
    return appConfigData?.fetchAppConfig.filter((config: any) => config.key === 'levels').map((config: any) => JSON.parse(config.value))[0] ?? [];
  }, [appConfigData]);

  const [userToDisplayPointHistory, setUserToDisplayPointHistory] = useState<any>(null);
  const [isDisplayPointHistoryOpen, setIsDisplayPointHistoryOpen] = useState(false);

  const [sortBy, setSortBy] = React.useState<any>(null);

  const handleSort = useCallback((sorting: any) => {
    setSortBy(sorting);
  }, []);

  useEffect(() => {
    variables.current = {
      ...variables.current,
      pagination: {
        take: rowsPerPage,
        skip: page * rowsPerPage,
      },
    };
    if (sortBy) {
      variables.current = {
        ...variables.current,
        orderBy: sortBy,
      }
    }
    refetch(variables.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sortBy]);

  useEffect(() => {
    variablesCities.current = {
      ...variablesCities.current,
      pagination: {
        take: rowsPerPageCities,
        skip: pageCities * rowsPerPageCities,
      },
    };
    refetchCities(variablesCities.current);
  }, [pageCities, rowsPerPageCities, refetchCities]);

  const onFilter = useCallback((filter: LeaderboardFilterData) => {
    variables.current = {
      ...variables.current,
      username: filter.filters.find((filter) => filter.field === 'username')?.value,
      level: filter.filters.find((filter) => filter.field === 'level')?.value,
      regionCountry: filter.filters.find((filter) => filter.field === 'regionCountry')?.value,
      regionCity: filter.filters.find((filter) => filter.field === 'regionCity')?.value,
    };
    refetch(variables.current);
    variablesCities.current = {
      ...variablesCities.current,
      regionCountry: filter.filters.find((filter) => filter.field === 'regionCountry')?.value,
    };
    refetchCities(variablesCities.current);
  }, [refetch, refetchCities]);

  if (loading || loadingCities) return <h4>Loading...</h4>;

  const leaderboard = data.getLeaderboardForAdmin.leaderboard;
  const total = data.getLeaderboardForAdmin.total;
  return (
    <Box sx={{ flexGrow: 1 }}>
      <Popover open={isDisplayPointHistoryOpen} handleClose={() => setIsDisplayPointHistoryOpen(false)}>
        <PointHistory username={userToDisplayPointHistory} handleClose={() => setIsDisplayPointHistoryOpen(false)} />
      </Popover>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Item>
            <Typography sx={{ m: 2 }} variant="h4">Point History</Typography>
          </Item>
        </Grid>
        <Grid item xs={12}>
          <Item>
            <LeaderboardFilter onFilter={onFilter} />
          </Item>
        </Grid>
        <Grid item xs={12}>
          <Item>
            <DownloadCsvButton
              path="user/export-leaderboard-csv"
              filter={{
                regionCountry: variables.current.regionCountry,
                regionCity: variables.current.regionCity,
              }}
            />
            <Table
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              onSort={handleSort}
              loading={loading}
              columns={[
                {
                  label: "Username",
                  key: "username",
                  rowRender: (row) => {
                    return (
                      <Button onClick={() => {
                        setUserToDisplayPointHistory(row.username);
                        setIsDisplayPointHistoryOpen(true);
                      }}>
                        <p style={{ textTransform: 'none' }}>{row.username}</p>
                      </Button>
                    );
                  },
                },
                {
                  label: "First Name",
                  key: "firstName",
                },
                {
                  label: "Last Name",
                  key: "lastName",
                },
                {
                  label: "Email",
                  key: "email",
                },
                {
                  label: "Number of Recommendations",
                  key: "numberOfRecommendations",
                  skipSorting: true,
                },
                {
                  label: "Number of Referrals",
                  key: "numberOfReferrals",
                  skipSorting: true,
                  rowRender: (row) => {
                    return row.levelInfo.referredUsers;
                  },
                },
                {
                  label: "Number of Referrals Who Recommended",
                  key: "numberOfReferralsWhoRecommended",
                  skipSorting: true,
                  rowRender: (row) => {
                    return row.levelInfo.referredUsersWhoRecommended;
                  },
                },
                {
                  label: "Level",
                  key: "levelInfo",
                  skipSorting: true,
                  rowRender: (row) => {
                    const userLevels = [...userLevelConfig];
                    for (var i = 0; i < userLevels.length; i++) {
                      const level = userLevels[i];
                      const referredUsersIndex = level.badges.findIndex((badge: any) => badge.id === 'referred-users');
                      if (referredUsersIndex !== -1) {
                        level.badges[referredUsersIndex] = {
                          ...level.badges[referredUsersIndex],
                          count: row.levelInfo.referredUsers,
                        };
                        userLevels[i] = { ...level, badges: [...level.badges] };
                      }
                      const referredUsersWhoRecommendedIndex = level.badges.findIndex((badge: any) => badge.id === 'referred-users-who-recommended');
                      if (referredUsersWhoRecommendedIndex !== -1) {
                        level.badges[referredUsersWhoRecommendedIndex] = {
                          ...level.badges[referredUsersWhoRecommendedIndex],
                          count: row.levelInfo.referredUsersWhoRecommended,
                        };
                        userLevels[i] = { ...level, badges: [...level.badges] };
                      }
                      const loginStreakIndex = level.badges.findIndex((badge: any) => badge.id === 'login-streak');
                      if (loginStreakIndex !== -1) {
                        level.badges[loginStreakIndex] = {
                          ...level.badges[loginStreakIndex],
                          count: row.levelInfo.longestStreak,
                        };
                        userLevels[i] = { ...level, badges: [...level.badges] };
                      }
                      const joinedATeamIndex = level.badges.findIndex((badge: any) => badge.id === 'joined-a-team');
                      if (joinedATeamIndex !== -1) {
                        level.badges[joinedATeamIndex] = {
                          ...level.badges[joinedATeamIndex],
                          count: row.levelInfo.joinedATeam ? 1 : 0,
                        };
                        userLevels[i] = { ...level, badges: [...level.badges] };
                      }
                      const commentedInATeamIndex = level.badges.findIndex((badge: any) => badge.id === 'commented-in-a-team');
                      if (commentedInATeamIndex !== -1) {
                        level.badges[commentedInATeamIndex] = {
                          ...level.badges[commentedInATeamIndex],
                          count: row.levelInfo.commentedInATeam ? 1 : 0,
                        };
                        userLevels[i] = { ...level, badges: [...level.badges] };
                      }
                    }
                    const level = userLevels.reverse().find((level: any) => level.badges.every((badge: any) => {
                      return badge.count >= badge.targetCount;
                    }));
                    return level ? level.name['en'] : 'Beginner';
                  },
                },
                {
                  label: "Total Points",
                  key: "totalPoints",
                },

              ]}
              total={total}
              rows={leaderboard}
              initialSorting={{
                totalPoints: 'desc',
              }}
            />
          </Item>
        </Grid>
        <Grid item xs={12}>
          <Item>
            <Typography sx={{ m: 2 }} variant="h4">Points by Cities</Typography>
          </Item>
        </Grid>
        <Grid item xs={12}>
          <Item>
            <Table
              page={pageCities}
              setPage={setPageCities}
              rowsPerPage={rowsPerPageCities}
              setRowsPerPage={setRowsPerPageCities}
              loading={loadingCities}
              columns={[
                {
                  label: "Rank",
                  key: "rank",
                },
                {
                  label: "City",
                  key: "regionCity",
                },
                {
                  label: "Total Points",
                  key: "sum",
                },
              ]}
              total={dataCities.getLeaderboardForCities.total}
              rows={dataCities.getLeaderboardForCities.cities}
            />
          </Item>
        </Grid>
      </Grid>
    </Box>
  );
};